import { createScreenText } from 'helpers/screenText';
const st = createScreenText('sinh_bo_so_qr');

// src/pages/SinhBoSoQr/types.ts
// TypeScript type definitions cho Sinh Bộ Số & Mã QR

// Enum trạng thái QR (từ requirements)
export type TrangThaiQR = 'Pending' | 'Generated' | 'DangChoSinh' | 'DaSinhBoSoVaQR';

// Loại vé mẫu
export type LoaiVeMau = 'Bac' | 'Trung' | 'Nam';

// Trạng thái job async. "stalled" = worker xử lý bị crash/OOM giữa chừng (StalledJobWatchdogService
// phát hiện do mất heartbeat), khác "failed" ở nguồn gốc phát hiện nhưng xử lý retry giống hệt nhau.
// "cancelled" = người dùng chủ động bấm hủy (xem nút "Hủy" trong popup tiến trình).
export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'stalled' | 'cancelled';

// Entity kỳ phát hành từ API

export interface KyPhatHanh {
  id: number;
  periodName: string;              // VD: "K3-04/2026"
  ticketType: LoaiVeMau;
  ticketQuantity: number;          // VD: 16000000
  useDate: string;         // ISO date string
  qrStatus: TrangThaiQR;
  // Dữ liệu cấu hình (đọc từ DB, không nhập ở màn này)
  numberRangeStart: string;
  numberRangeEnd: string;
  ticketSymbols: string[];      // Array of strings: ["A", "B", "C"]
  createdAt?: string;
  createdBy?: number;
  lastModifiedAt?: string;
  lastModifiedBy?: number;
}

// Job tracking entity
export interface QrGenerationJob {
  jobId: string;              // UUID
  lotteryPeriodId: number;
  status: JobStatus;
  totalTickets: number;
  processedTickets: number;
  progressPercent: number;    // 0.00 → 100.00
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

// WebSocket progress message format
export interface JobProgressMessage {
  jobId: string;
  status: JobStatus;
  processedTickets: number;
  totalTickets: number;
  progressPercent: number;
  errorMessage?: string;
}

// API response wrapper (theo format chuẩn của project)
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
  errors?: Array<{ field: string; message: string }>;
}

// Response từ POST /generate-qr
export interface SinhBoSoQrResponse {
  jobId: string;
  status: 'pending';
  message: string;
}

// Filters cho danh sách
export interface SinhBoSoQrFilters {
  searchTerm?: string;
  trangThaiQR?: TrangThaiQR | 'All';
}

// Pagination
export interface SinhBoSoQrPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

// Modal states
export interface ModalState {
  confirm: boolean;
}

// Display labels
export const TRANG_THAI_QR_LABELS: Record<TrangThaiQR, string> = {
  Pending: st('cho_sinh_option'),
  Generated: st('da_sinh_qr_option'),
  DangChoSinh: st('dang_sinh_option'),
  DaSinhBoSoVaQR: st('da_sinh_qr_option'),
};

export const LOAI_VE_MAU_LABELS: Record<LoaiVeMau, string> = {
  Bac: st('mien_bac_text'),
  Trung: st('mien_trung_text'),
  Nam: st('mien_nam_text'),
};

export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  pending: st('cho_xu_ly_text'),
  processing: st('dang_xu_ly_text'),
  completed: st('hoan_thanh_text'),
  failed: st('that_bai_text'),
  stalled: st('bi_dung_dot_ngot_text'),
  cancelled: st('da_huy_text'),
};

// Backend (ticket-service) luu Status cua QrGenerationJob/PrintPackageExportJob/QrImageExportJob
// dang int (enum JobStatuses: Pending=1, Processing=2, Completed=3, Failed=4, Stalled=5,
// Cancelled=6) — mọi nơi FE doc status tu API/SignalR payload deu phai chuyen ve string qua ham
// nay TRUOC khi luu vao Redux state, de toan bo logic so sanh string hien co ('completed', 'failed'...)
// khong phai sua lai.
const JOB_STATUS_NUM_TO_STRING: Record<number, JobStatus> = {
  1: 'pending',
  2: 'processing',
  3: 'completed',
  4: 'failed',
  5: 'stalled',
  6: 'cancelled',
};

export const toJobStatus = (raw: unknown): JobStatus => {
  if (typeof raw === 'number') return JOB_STATUS_NUM_TO_STRING[raw] ?? 'pending';
  if (typeof raw === 'string') {
    const asNumber = Number(raw);
    if (!Number.isNaN(asNumber) && JOB_STATUS_NUM_TO_STRING[asNumber]) return JOB_STATUS_NUM_TO_STRING[asNumber];
    if (raw in JOB_STATUS_LABELS) return raw as JobStatus;
  }
  return 'pending';
};
